import React from "react";
import dayjs from "dayjs";
import { toast } from "sonner";

import { DataTable, DataTableColumn } from "../../components/ui/data-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Spinner } from "../../components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  listMonthlyCurations,
} from "../admin/aicuration/persistence";
import {
  CUSTOMER_CURATION_TABLE_COLUMNS,
  CurationTableRow,
} from "../admin/aicuration/tableColumns";
import { MonthlyCurationRecord, ProductCategory } from "../admin/aicuration/types";
import {
  money,
  normalizeProductKey,
  sanitizeCustomerFacingReason,
} from "../admin/aicuration/utils";

type CurationResultAnalysisPageProps = {
  affiliation?: string;
};

type AnalysisRow = {
  key: number;
  product_name: string;
  category: ProductCategory;
  quantity: number;
  total_price: number;
  total_quantity: number;
  outbound_quantity: number;
  request_count: number;
  remaining_stock_quantity: number;
  evidence: string;
};

const ANALYSIS_COLUMNS: DataTableColumn<AnalysisRow>[] = [
  { title: "품목명", dataIndex: "product_name", width: 220, align: "left" },
  {
    title: "카테고리",
    dataIndex: "category",
    width: 90,
    align: "left",
    render: (value: ProductCategory) => (value === "beverage" ? "음료" : "스낵"),
  },
  { title: "추천 수량", dataIndex: "quantity", width: 90, align: "left" },
  {
    title: "예상 금액",
    dataIndex: "total_price",
    width: 120,
    align: "left",
    render: (value: number) => money(value),
  },
  { title: "누적 판매", dataIndex: "total_quantity", width: 100, align: "left" },
  { title: "출고", dataIndex: "outbound_quantity", width: 90, align: "left" },
  { title: "신청", dataIndex: "request_count", width: 90, align: "left" },
  {
    title: "잔여 수량",
    dataIndex: "remaining_stock_quantity",
    width: 100,
    align: "left",
  },
  { title: "분석 메모", dataIndex: "evidence", width: 360, align: "left" },
];

const formatMonthLabel = (monthKey: string) => {
  const parsed = dayjs(`${monthKey}-01`);
  return parsed.isValid() ? parsed.format("YYYY년 M월") : monthKey;
};

const formatDateTime = (value: any) => {
  if (!value) return "-";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : Number.isFinite(value?.seconds)
        ? new Date(value.seconds * 1000)
        : value instanceof Date
          ? value
          : null;
  if (!date) return "-";
  return dayjs(date).format("YYYY-MM-DD HH:mm");
};

const buildEvidence = (row: AnalysisRow) => {
  const parts: string[] = [];
  if (row.total_quantity > 0) {
    parts.push(`누적 판매 ${row.total_quantity}개`);
  }
  if (row.outbound_quantity > 0) {
    parts.push(`출고 ${row.outbound_quantity}개`);
  }
  if (row.request_count > 0) {
    parts.push(`간식신청 ${row.request_count}건`);
  }
  if (row.remaining_stock_quantity > 0) {
    parts.push(`입출고 기준 잔여 ${row.remaining_stock_quantity}개`);
  }
  return parts.join(" + ") || "판매 외 추가 근거 없음";
};

const buildMonthlyAnalysis = (record: MonthlyCurationRecord | null) => {
  if (!record) return null;

  const items = record.debate_result?.finalConsensus?.items || [];
  const topProducts = record.dataset_summary?.top_products || [];
  const productMap = new Map(
    topProducts.map((product) => [
      normalizeProductKey(product.product_name),
      product,
    ]),
  );

  const rows: AnalysisRow[] = items.map((item, index) => {
    const matched = productMap.get(normalizeProductKey(item.product_name));
    const row: AnalysisRow = {
      key: index + 1,
      product_name: item.product_name,
      category: item.category,
      quantity: item.quantity,
      total_price: item.total_price,
      total_quantity: matched?.total_quantity || 0,
      outbound_quantity: matched?.outbound_quantity || 0,
      request_count: matched?.request_count || 0,
      remaining_stock_quantity: matched?.remaining_stock_quantity || 0,
      evidence: "",
    };
    return {
      ...row,
      evidence: buildEvidence(row),
    };
  });

  const totalQuantity = items.reduce(
    (sum, item) => sum + Math.max(0, Number(item.quantity || 0)),
    0,
  );
  const snackCount = items.filter((item) => item.category === "snack").length;
  const beverageCount = items.filter(
    (item) => item.category === "beverage",
  ).length;
  const requestedCount = rows.filter((row) => row.request_count > 0).length;
  const outboundCount = rows.filter((row) => row.outbound_quantity > 0).length;
  const remainingCount = rows.filter(
    (row) => row.remaining_stock_quantity > 0,
  ).length;
  const demandBackedCount = rows.filter(
    (row) =>
      row.total_quantity > 0 || row.outbound_quantity > 0 || row.request_count > 0,
  ).length;
  const totalCost = record.debate_result.finalConsensus.estimated_total_cost;
  const budget = record.debate_result.finalConsensus.target_budget;
  const top3Cost = [...items]
    .sort((a, b) => b.total_price - a.total_price)
    .slice(0, 3)
    .reduce((sum, item) => sum + item.total_price, 0);
  const top3Share = totalCost > 0 ? Math.round((top3Cost / totalCost) * 100) : 0;

  const insights: string[] = [];

  if (budget) {
    const gap = budget - totalCost;
    insights.push(
      gap >= 0
        ? `예상 총액 ${money(totalCost)}으로 목표 예산 ${money(budget)} 안에서 ${money(gap)} 여유가 있습니다.`
        : `예상 총액 ${money(totalCost)}으로 목표 예산 ${money(budget)}을 ${money(Math.abs(gap))} 초과했습니다.`,
    );
  } else {
    insights.push(`이번 월 큐레이션은 총 ${money(totalCost)} 규모로 제안되었습니다.`);
  }

  insights.push(
    `전체 ${items.length}개 품목 중 ${demandBackedCount}개가 판매·출고·신청 이력으로 수요 근거가 확인됩니다.`,
  );
  insights.push(
    `구성은 스낵 ${snackCount}개, 음료 ${beverageCount}개이며 비용 비중은 ${record.debate_result.finalConsensus.snack_beverage_ratio || "-"}입니다.`,
  );
  insights.push(
    `상위 3개 품목이 총 예상 금액의 ${top3Share}%를 차지해 ${
      top3Share >= 55 ? "집중도가 높은 편" : "구성이 비교적 분산된 편"
    }입니다.`,
  );
  insights.push(
    `${requestedCount}개 품목은 간식 신청 이력이 있고, ${outboundCount}개 품목은 실제 출고 이력이 있으며, ${remainingCount}개 품목은 입출고 기준 잔여 수량이 남아 있습니다.`,
  );

  return {
    rows,
    insights,
    cards: [
      { label: "저장 품목 수", value: `${items.length}개` },
      { label: "추천 총 수량", value: `${totalQuantity.toLocaleString("ko-KR")}개` },
      { label: "예상 총액", value: money(totalCost) },
      { label: "요청 연계 품목", value: `${requestedCount}개` },
      { label: "출고 검증 품목", value: `${outboundCount}개` },
      { label: "운영 가능 품목", value: `${remainingCount}개` },
    ],
  };
};

const CurationResultAnalysisPage: React.FC<CurationResultAnalysisPageProps> = ({
  affiliation = "",
}) => {
  const [records, setRecords] = React.useState<MonthlyCurationRecord[]>([]);
  const [selectedMonthKey, setSelectedMonthKey] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!affiliation) {
      setRecords([]);
      setSelectedMonthKey("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadRecords = async () => {
      setLoading(true);
      try {
        const nextRecords = await listMonthlyCurations(affiliation);
        if (cancelled) return;
        setRecords(nextRecords);
        setSelectedMonthKey((prev) => {
          if (prev && nextRecords.some((record) => record.month_key === prev)) {
            return prev;
          }
          return nextRecords[0]?.month_key || "";
        });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          toast.error("월별 큐레이션 결과를 불러오지 못했습니다.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, [affiliation]);

  const selectedRecord = React.useMemo(
    () =>
      records.find((record) => record.month_key === selectedMonthKey) || null,
    [records, selectedMonthKey],
  );

  const resultRows = React.useMemo<CurationTableRow[]>(() => {
    if (!selectedRecord) return [];
    return selectedRecord.debate_result.finalConsensus.items.map((item, index) => ({
      key: index + 1,
      rank: index + 1,
      ...item,
      reason: sanitizeCustomerFacingReason(item.reason),
    }));
  }, [selectedRecord]);

  const monthlyAnalysis = React.useMemo(
    () => buildMonthlyAnalysis(selectedRecord),
    [selectedRecord],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold">큐레이션 결과/분석</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          저장된 월별 큐레이션 결과를 조회하고, 수요 근거와 구성 인사이트를
          함께 확인할 수 있습니다.
        </p>
      </div>

      {loading ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
          <Spinner size="lg" />
          <span className="text-sm text-slate-500">
            월별 큐레이션 결과를 불러오는 중...
          </span>
        </div>
      ) : records.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>저장된 큐레이션 없음</CardTitle>
            <CardDescription>
              아직 확정 저장된 월별 큐레이션 결과가 없습니다.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            AI 큐레이션 메뉴에서 월을 선택한 뒤 결과를 확정하면 이 화면에서 월별
            조회와 분석이 가능합니다.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="flex flex-col gap-4 pt-6 md:flex-row md:items-end md:justify-between">
              <div className="w-full max-w-xs space-y-2">
                <div className="text-sm font-medium">조회 월</div>
                <Select value={selectedMonthKey} onValueChange={setSelectedMonthKey}>
                  <SelectTrigger>
                    <SelectValue placeholder="월 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {records.map((record) => (
                      <SelectItem key={record.month_key} value={record.month_key}>
                        {formatMonthLabel(record.month_key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 text-sm text-muted-foreground">
                <div>고객사: {selectedRecord?.affiliation || "-"}</div>
                <div>최종 저장: {formatDateTime(selectedRecord?.updated_at)}</div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="results" className="space-y-4">
            <TabsList>
              <TabsTrigger value="results">월 별 큐레이션 결과</TabsTrigger>
              <TabsTrigger value="analysis">월 별 큐레이션 분석</TabsTrigger>
            </TabsList>

            <TabsContent value="results" className="space-y-4">
              {selectedRecord && (
                <>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>대상 월</CardDescription>
                        <CardTitle className="text-2xl">
                          {formatMonthLabel(selectedRecord.month_key)}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>예상 총액</CardDescription>
                        <CardTitle className="text-2xl">
                          {money(
                            selectedRecord.debate_result.finalConsensus
                              .estimated_total_cost,
                          )}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>스낵:음료 비율</CardDescription>
                        <CardTitle className="text-2xl">
                          {selectedRecord.debate_result.finalConsensus
                            .snack_beverage_ratio || "-"}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>요구사항 및 저장 정보</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      <div>
                        <div className="font-semibold text-foreground">자연어 요구사항</div>
                        <p>{selectedRecord.requirement || "추가 요구사항 없음"}</p>
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">최종 합의 결론</div>
                        <p>
                          {selectedRecord.debate_result.finalConsensus
                            .discussion_summary || "-"}
                        </p>
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">최종 가정</div>
                        <ul className="list-disc space-y-1 pl-5">
                          {selectedRecord.debate_result.finalConsensus.assumptions
                            .length > 0 ? (
                            selectedRecord.debate_result.finalConsensus.assumptions.map(
                              (assumption, index) => (
                                <li key={`${assumption}-${index}`}>{assumption}</li>
                              ),
                            )
                          ) : (
                            <li>가정 정보 없음</li>
                          )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>월별 큐레이션 결과 테이블</CardTitle>
                      <CardDescription>
                        저장된 최종 큐레이션 결과를 월 기준으로 다시 확인할 수 있습니다.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        columns={CUSTOMER_CURATION_TABLE_COLUMNS}
                        dataSource={resultRows}
                        pagination={false}
                        search={{
                          keys: [
                            "product_name",
                            "category",
                            "reference_source",
                            "reason",
                            "coupang_search_url",
                          ],
                          placeholder: "품목/참조소스/추천근거 검색",
                        }}
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            <TabsContent value="analysis" className="space-y-4">
              {selectedRecord && monthlyAnalysis && (
                <>
                  <div className="grid gap-4 md:grid-cols-3">
                    {monthlyAnalysis.cards.map((card) => (
                      <Card key={card.label}>
                        <CardHeader className="pb-2">
                          <CardDescription>{card.label}</CardDescription>
                          <CardTitle className="text-2xl">{card.value}</CardTitle>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>월 별 큐레이션 분석 인사이트</CardTitle>
                      <CardDescription>
                        저장된 결과를 판매/출고/신청/운영 가능성 기준으로 해석한
                        요약입니다.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                        {monthlyAnalysis.insights.map((insight) => (
                          <li key={insight}>{insight}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>품목별 상세 분석</CardTitle>
                      <CardDescription>
                        저장된 품목별로 판매, 출고, 신청, 잔여 수량 근거를 함께
                        확인합니다.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        columns={ANALYSIS_COLUMNS}
                        dataSource={monthlyAnalysis.rows}
                        pagination={false}
                        search={{
                          keys: ["product_name", "evidence"],
                          placeholder: "품목/분석 메모 검색",
                        }}
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default CurationResultAnalysisPage;
